import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNotificationContext } from '../contexts/NotificationContext';
import { useWebSocket } from '../contexts/WebSocketContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const Timetables = () => {
  const { showSuccess, showError } = useNotificationContext();
  const [sessions, setSessions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [classes, setClasses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [filters, setFilters] = useState({
    department: '',
    academicYear: '',
    semester: 1,
    class: ''
  });
  const [formData, setFormData] = useState({
    course: '',
    teacher: '',
    class: '',
    room: '',
    type: 'LECTURE',
    dayOfWeek: 1,
    timeSlot: 1,
    semester: 1,
    group: ''
  });
  const [availableCourses, setAvailableCourses] = useState([]);
  const [tooltip, setTooltip] = useState({ show: false, content: '', x: 0, y: 0 });
  const [conflictAlert, setConflictAlert] = useState({ show: false, message: '' });
  const [occupiedRooms, setOccupiedRooms] = useState([]);
  const [draggedSession, setDraggedSession] = useState(null);
  const [dragOverSlot, setDragOverSlot] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, sessionId: null });
  const [validationResults, setValidationResults] = useState(null);
  const [showValidation, setShowValidation] = useState(false);
  const [academicYearConfig, setAcademicYearConfig] = useState(null);
  const [timeSlots, setTimeSlots] = useState([
    '8h30-10h00', '10h10-11h40', '11h50-13h20',
    '13h30-15h00', '15h10-16h40', '16h50-18h20'
  ]);
  const [days, setDays] = useState(['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi']);

  const sessionTypes = {
    'LECTURE': 'Cours',
    'TUTORIAL': 'TD',
    'PRACTICAL': 'TP',
    'INTEGRATED': 'CI'
  };

  const getTeacherColor = (teacherId) => {
    const colors = [
      'bg-red-100 text-red-800', 'bg-blue-100 text-blue-800', 'bg-green-100 text-green-800',
      'bg-yellow-100 text-yellow-800', 'bg-purple-100 text-purple-800', 'bg-pink-100 text-pink-800',
      'bg-indigo-100 text-indigo-800', 'bg-gray-100 text-gray-800'
    ];
    const hash = teacherId?.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0) || 0;
    return colors[Math.abs(hash) % colors.length];
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (filters.department && filters.academicYear) {
      fetchClassesByDepartmentAndYear();
      fetchAcademicYearConfig();
    }
  }, [filters.department, filters.academicYear]);

  useEffect(() => {
    if (classes.length > 0) {
      fetchSessions();
    }
  }, [classes, filters.semester]);

  const handleWebSocketMessage = useCallback((data) => {
    if (data.type === 'connection') {
      console.log('Connection message:', data.message);
    } else if (data.type === 'session_updated') {
      console.log('🔄 Session update reçu:', data.data?.action, data.data);
      if (classes.length > 0) {
        setTimeout(() => {
          console.log('🔄 Refreshing sessions list...');
          fetchSessions();
        }, 100);
      }
    }
  }, [classes.length]);

  const { isConnected } = useWebSocket('sessions', handleWebSocketMessage);

  const fetchData = async () => {
    try {
      const [departmentsRes, academicYearsRes, coursesRes, teachersRes, roomsRes] = await Promise.all([
        axios.get(`${API_URL}/api/departments`),
        axios.get(`${API_URL}/api/academic-years`),
        axios.get(`${API_URL}/api/courses`),
        axios.get(`${API_URL}/api/teachers`),
        axios.get(`${API_URL}/api/rooms`)
      ]);

      setDepartments(departmentsRes.data);
      setAcademicYears(academicYearsRes.data);
      setCourses(coursesRes.data);
      setTeachers(teachersRes.data);
      setRooms(roomsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      setLoading(false);
    }
  };

  const fetchClassesByDepartmentAndYear = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.department) params.append('department', filters.department);
      if (filters.academicYear) params.append('academicYear', filters.academicYear);
      params.append('limit', '10000');
      params.append('_t', Date.now());
      
      const url = `${API_URL}/api/classes?${params.toString()}`;
      const response = await axios.get(url, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      setClasses(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des classes:', error);
    }
  };

  const fetchAcademicYearConfig = async () => {
    try {
      if (!filters.academicYear) {
        return;
      }

      const response = await axios.get(
        `${API_URL}/api/academic-year-config/${filters.academicYear}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        }
      );

      const config = response.data.data || response.data;
      
      if (!config) {
        setAcademicYearConfig(null);
        return;
      }

      setAcademicYearConfig(config);

      // Build timeslots from config
      if (config.timeslots && config.timeslots.length > 0) {
        const slots = config.timeslots
          .sort((a, b) => a.order - b.order)
          .map(slot => `${slot.startTime}-${slot.endTime}`);
        setTimeSlots(slots);
      }

      // Build days from config - FILTER BY isWorking
      if (config.daysOfWeek && config.daysOfWeek.length > 0) {
        const daysList = config.daysOfWeek
          .filter(day => day.isWorking === true)
          .sort((a, b) => a.order - b.order)
          .map(day => day.dayName);
        setDays(daysList);
      }
    } catch (error) {
      console.error('Erreur lors du chargement de la configuration:', error);
      setAcademicYearConfig(null);
    }
  };

  const fetchSessions = async () => {
    try {
      const classIds = classes.map(cls => cls._id);
      const allSessions = [];

      for (const classId of classIds) {
        const response = await axios.get(`${API_URL}/api/sessions?class=${classId}&semester=${filters.semester}&limit=10000&_t=${Date.now()}`);
        const sessionsData = Array.isArray(response.data) ? response.data : response.data.data || [];
        allSessions.push(...sessionsData);
      }

      setSessions(allSessions);
    } catch (error) {
      console.error('Erreur lors du chargement des sessions:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        room: formData.room || null
      };

      if (editingSession) {
        await axios.put(`${API_URL}/api/sessions/${editingSession._id}`, submitData);
      } else {
        await axios.post(`${API_URL}/api/sessions`, submitData);
      }
      closeModal();
      showSuccess(editingSession?._id ? 'Séance modifiée avec succès' : 'Séance ajoutée avec succès');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      const errorMessage = error.response?.data?.message || 'Erreur lors de la sauvegarde de la séance';

      if (errorMessage.includes('Conflit')) {
        setConflictAlert({ show: true, message: errorMessage });
      } else {
        showError(errorMessage);
      }
    }
  };

  const closeConflictAlert = () => {
    setConflictAlert({ show: false, message: '' });
  };

  const checkOccupiedRooms = async (dayOfWeek, timeSlot, semester, excludeSessionId = null) => {
    try {
      const params = new URLSearchParams({
        dayOfWeek: dayOfWeek.toString(),
        timeSlot: timeSlot.toString(),
        semester: semester.toString(),
        limit: '10000',
        _t: Date.now().toString()
      });

      const response = await axios.get(`${API_URL}/api/sessions?${params}`);
      const sessionsData = Array.isArray(response.data) ? response.data : response.data.data || [];

      const occupiedRoomIds = sessionsData
        .filter(session => {
          if (excludeSessionId && session._id === excludeSessionId) {
            return false;
          }
          return session.dayOfWeek === dayOfWeek &&
            session.timeSlot === timeSlot &&
            session.semester === semester &&
            session.room && session.room._id;
        })
        .map(session => session.room._id)
        .filter((roomId, index, array) => array.indexOf(roomId) === index);

      console.log('Salles occupées pour', { dayOfWeek, timeSlot, semester }, ':', occupiedRoomIds);
      setOccupiedRooms(occupiedRoomIds);
    } catch (error) {
      console.error('Erreur lors de la vérification des salles:', error);
      setOccupiedRooms([]);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/sessions/${id}`);
      showSuccess('Séance supprimée avec succès');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      showError('Erreur lors de la suppression de la séance');
    }
  };

  const confirmDelete = (sessionId) => {
    setDeleteConfirm({ show: true, sessionId });
    closeModal();
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirm.sessionId) {
      await handleDelete(deleteConfirm.sessionId);
    }
    setDeleteConfirm({ show: false, sessionId: null });
  };

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, sessionId: null });
  };

  const openModal = async (session = null) => {
    setEditingSession(session && session._id ? session : null);

    let newFormData;

    if (session && session._id) {
      const selectedClass = classes.find(cls => cls._id === session.class._id);
      if (selectedClass) {
        await loadCoursesForClass(selectedClass);
      }
      newFormData = {
        course: session.course?._id || '',
        teacher: session.teacher?._id || '',
        class: session.class?._id || '',
        room: session.room?._id || '',
        type: session.type || 'LECTURE',
        dayOfWeek: session.dayOfWeek || 1,
        timeSlot: session.timeSlot || 1,
        semester: session.semester || filters.semester,
        group: session.group || ''
      };
    } else if (session && session.class) {
      await loadCoursesForClass(session.class);
      newFormData = {
        course: availableCourses.length > 0 ? availableCourses[0]._id : '',
        teacher: teachers.length > 0 ? teachers[0]._id : '',
        class: session.class._id,
        room: '',
        type: 'PRACTICAL',
        dayOfWeek: session.dayOfWeek,
        timeSlot: session.timeSlot,
        semester: session.semester,
        group: session.group || ''
      };
    } else {
      if (classes.length > 0) {
        await loadCoursesForClass(classes[0]);
      }
      newFormData = {
        course: availableCourses.length > 0 ? availableCourses[0]._id : '',
        teacher: teachers.length > 0 ? teachers[0]._id : '',
        class: classes.length > 0 ? classes[0]._id : '',
        room: '',
        type: 'LECTURE',
        dayOfWeek: 1,
        timeSlot: 1,
        semester: filters.semester,
        group: ''
      };
    }

    if (newFormData.dayOfWeek && newFormData.timeSlot && newFormData.semester) {
      const excludeId = editingSession?._id || null;
      await checkOccupiedRooms(newFormData.dayOfWeek, newFormData.timeSlot, newFormData.semester, excludeId);
    }
    setFormData(newFormData);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSession(null);
    setOccupiedRooms([]);
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    const newFormData = {
      ...formData,
      [name]: ['dayOfWeek', 'timeSlot', 'semester'].includes(name) ? Number(value) : value
    };

    setFormData(newFormData);

    if (name === 'class' && value) {
      const selectedClass = classes.find(cls => cls._id === value);
      if (selectedClass) {
        await loadCoursesForClass(selectedClass);
        setFormData(prev => ({
          ...prev,
          class: value,
          course: availableCourses.length > 0 ? availableCourses[0]._id : ''
        }));
      }
    }

    if (['dayOfWeek', 'timeSlot', 'semester'].includes(name)) {
      const dayOfWeek = name === 'dayOfWeek' ? Number(value) : newFormData.dayOfWeek;
      const timeSlot = name === 'timeSlot' ? Number(value) : newFormData.timeSlot;
      const semester = name === 'semester' ? Number(value) : newFormData.semester;

      if (dayOfWeek && timeSlot && semester) {
        const excludeId = editingSession?._id || null;
        await checkOccupiedRooms(dayOfWeek, timeSlot, semester, excludeId);
      }
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: name === 'semester' ? Number(value) : value
    });
  };

  const loadCoursesForClass = async (selectedClass) => {
    try {
      if (selectedClass.department && selectedClass.track) {
        const response = await axios.get(`${API_URL}/api/courses?department=${selectedClass.department._id}&track=${selectedClass.track._id}&semester=${filters.semester}`);
        setAvailableCourses(response.data);
      } else {
        setAvailableCourses([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des cours:', error);
      setAvailableCourses([]);
    }
  };

  const getSessionsForClassAndSlot = (classId, dayIndex, timeSlotIndex) => {
    return sessions.filter(session =>
      session.class._id === classId &&
      session.dayOfWeek === dayIndex + 1 &&
      session.timeSlot === timeSlotIndex + 1
    );
  };

  const showTooltip = (e, session) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const tooltipContent = (
      <div className="p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg">
        <div><strong>Matière:</strong> {session.course?.name || 'N/A'} ({session.course?.code || 'N/A'})</div>
        <div><strong>Classe:</strong> {session.class?.name || 'N/A'}</div>
        <div><strong>Enseignant:</strong> {session.teacher ? `${session.teacher.firstName} ${session.teacher.lastName}` : 'N/A'}</div>
        <div><strong>Salle:</strong> {session.room?.name || 'N/A'}</div>
        <div><strong>Type:</strong> {sessionTypes[session.type] || session.type}</div>
        {session.group && <div><strong>Groupe:</strong> {session.group}</div>}
      </div>
    );

    setTooltip({
      show: true,
      content: tooltipContent,
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
  };

  const hideTooltip = () => {
    setTooltip({ show: false, content: '', x: 0, y: 0 });
  };

  const handleDragStart = (e, session) => {
    setDraggedSession(session);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, classId, dayIndex, sessionNum) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverSlot({ classId, dayIndex, sessionNum });
  };

  const handleDragLeave = () => {
    setDragOverSlot(null);
  };

  const handleDrop = async (e, targetClassId, targetDayIndex, targetSessionNum) => {
    e.preventDefault();
    setDragOverSlot(null);

    if (!draggedSession) return;

    const updatedSession = {
      ...draggedSession,
      class: targetClassId,
      dayOfWeek: targetDayIndex + 1,
      timeSlot: targetSessionNum,
      room: draggedSession.room || null
    };

    try {
      const targetClass = classes.find(cls => cls._id === targetClassId);
      setSessions(prev => prev.map(session =>
        session._id === draggedSession._id
          ? { ...session, class: targetClass || { _id: targetClassId }, dayOfWeek: targetDayIndex + 1, timeSlot: targetSessionNum }
          : session
      ));

      const response = await axios.put(`${API_URL}/api/sessions/${draggedSession._id}`, updatedSession);
      showSuccess('Séance déplacée avec succès');
    } catch (error) {
      console.error('Erreur lors du déplacement:', error);
      fetchSessions();
      const errorMessage = error.response?.data?.message || 'Erreur lors du déplacement';
      if (errorMessage.includes('Conflit')) {
        setConflictAlert({ show: true, message: errorMessage });
      } else {
        showError(errorMessage);
      }
    }

    setDraggedSession(null);
  };

  const validateScheduleCompliance = async () => {
    try {
      const classId = filters.class || 'all';
      const response = await axios.get(`${API_URL}/api/sessions/validate-compliance?classId=${classId}&semester=${filters.semester}`);
      setValidationResults(response.data);
      setShowValidation(true);
    } catch (error) {
      console.error('Erreur lors de la validation:', error);
      showError('Erreur lors de la vérification de conformité');
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
        <p className="text-sm text-gray-600">Chargement...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-2">
      <div className="w-full">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div className="flex items-center gap-3 mb-4 sm:mb-0">
            <h1 className="text-xl font-semibold text-gray-900">Emplois du Temps</h1>
            <span className={`px-2 py-1 text-xs rounded-full ${isConnected
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
              }`}>
              {isConnected ? '🟢 Live' : '🔴 Manual'}
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={validateScheduleCompliance}
              className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 focus:ring-2 focus:ring-blue-500"
              disabled={classes.length === 0}
            >
              🔍 Vérifier Conformité
            </button>
            <button
              onClick={() => openModal()}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500"
              disabled={classes.length === 0}
            >
              + Ajouter Séance
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Département</label>
              <select
                name="department"
                value={filters.department}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Sélectionner un département</option>
                {departments.map(dept => (
                  <option key={dept._id} value={dept._id}>{dept.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Année universitaire</label>
              <select
                name="academicYear"
                value={filters.academicYear}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Sélectionner une année</option>
                {academicYears.map(year => (
                  <option key={year._id} value={year._id}>{year.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Semestre</label>
              <select
                name="semester"
                value={filters.semester}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {[1, 2].map(sem => (
                  <option key={sem} value={sem}>Semestre {sem}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Classe</label>
              <select
                name="class"
                value={filters.class}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Toutes les classes</option>
                {classes.map(cls => (
                  <option key={cls._id} value={cls._id}>{cls.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {classes.length > 0 ? (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table style={{ minWidth: `${200 + (timeSlots.length * 48 * days.length)}px`, tableLayout: 'fixed' }}>
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap border-r-2 border-gray-800" style={{ width: '1%' }}>Classes</th>
                    {days.map((day, dayIndex) => (
                      <th key={dayIndex} className={`px-1 py-2 text-center text-xs font-medium text-gray-500 uppercase ${dayIndex > 0 ? 'border-l-2 border-gray-800' : ''}`} style={{ width: `${timeSlots.length * 48}px` }}>
                        <div className="mb-1 text-xs">{day}</div>
                        <div className="grid gap-0" style={{gridTemplateColumns: `repeat(${timeSlots.length}, 1fr)`}}>
                          {timeSlots.map((slot, index) => (
                            <div key={index} className="text-xs font-normal text-gray-400">{index + 1}</div>
                          ))}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y-2 divide-gray-800">
                  {classes.filter(cls => !filters.class || cls._id === filters.class).map(cls => (
                    <tr key={cls._id}>
                      <td className="px-1 py-2 font-medium text-gray-900 text-xs whitespace-nowrap border-r-2 border-gray-800" style={{ width: '1%' }}>{cls.name}</td>
                      {days.map((day, dayIndex) => (
                        <td key={dayIndex} className={`px-1 py-2 ${dayIndex > 0 ? 'border-l-2 border-gray-800' : ''}`} style={{ width: `${timeSlots.length * 48}px` }}>
                          <div className="flex h-12">
                            {timeSlots.map((slot, sessionIndex) => {
                              const sessionNum = sessionIndex + 1;
                              const sessionsForSlot = getSessionsForClassAndSlot(cls._id, dayIndex, sessionIndex);
                              return (
                                <div
                                  key={sessionIndex}
                                  className={`border border-gray-300 p-0.5 min-h-[48px] relative ${dragOverSlot?.classId === cls._id &&
                                      dragOverSlot?.dayIndex === dayIndex &&
                                      dragOverSlot?.sessionNum === sessionNum ? 'bg-blue-100' : 'bg-gray-50'
                                    }`}
                                  style={{ width: '48px', height: '48px' }}
                                  onDragOver={(e) => handleDragOver(e, cls._id, dayIndex, sessionNum)}
                                  onDragLeave={handleDragLeave}
                                  onDrop={(e) => handleDrop(e, cls._id, dayIndex, sessionNum)}
                                >
                                  {sessionsForSlot.map(session => (
                                    <div
                                      key={session._id}
                                      className={`text-xs p-0.5 rounded cursor-pointer ${getTeacherColor(session.teacher?._id)} ${sessionsForSlot.length === 1 ? 'w-full h-full' : ''}`}
                                      draggable
                                      onDragStart={(e) => handleDragStart(e, session)}
                                      onMouseEnter={(e) => showTooltip(e, session)}
                                      onMouseLeave={hideTooltip}
                                      onClick={() => openModal(session)}
                                    >
                                      <div className="font-medium truncate" style={{ fontSize: '10px' }}>{(session.course?.code || 'N/A').substring(0, 3)}</div>
                                    </div>
                                  ))}
                                  {sessionsForSlot.length === 0 && (
                                    <button
                                      className="w-full h-full border-2 border-dashed border-gray-300 rounded text-gray-400 hover:border-primary-300 hover:text-primary-600 transition-colors text-xs"
                                      onClick={() => openModal({
                                        class: cls,
                                        dayOfWeek: dayIndex + 1,
                                        timeSlot: sessionNum,
                                        semester: filters.semester
                                      })}
                                    >
                                      +
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {sessions.filter(session => !session.room && (!filters.class || session.class._id === filters.class)).length > 0 && (
              <div className="border-t p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Séances sans salle assignée</h3>
                <div className="flex flex-wrap gap-2">
                  {sessions.filter(session => !session.room && (!filters.class || session.class._id === filters.class)).map(session => (
                    <div
                      key={session._id}
                      className={`px-3 py-2 rounded text-xs cursor-pointer ${getTeacherColor(session.teacher?._id)}`}
                      onMouseEnter={(e) => showTooltip(e, session)}
                      onMouseLeave={hideTooltip}
                      onClick={() => openModal(session)}
                      draggable
                      onDragStart={(e) => handleDragStart(e, session)}
                    >
                      {session.class.name} - {(session.course?.code || 'N/A').substring(0, 6)} - {days[session.dayOfWeek - 1]} {timeSlots[session.timeSlot - 1]}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">📅</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune classe trouvée</h3>
            <p className="text-gray-600">Veuillez sélectionner un département et une année universitaire pour afficher les emplois du temps.</p>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={closeModal}>
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-medium text-gray-900">{editingSession?._id ? 'Modifier Séance' : 'Ajouter Séance'}</h3>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">×</button>
              </div>
              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cours</label>
                    <select
                      name="course"
                      value={formData.course}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                      disabled={!formData.class}
                    >
                      <option value="">Sélectionner un cours</option>
                      {availableCourses.map(course => (
                        <option key={course._id} value={course._id}>{course.name} ({course.code})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Enseignant</label>
                    <select
                      name="teacher"
                      value={formData.teacher}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      {teachers.map(teacher => (
                        <option key={teacher._id} value={teacher._id}>
                          {teacher.firstName} {teacher.lastName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Classe</label>
                    <select
                      name="class"
                      value={formData.class}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      {classes.map(cls => (
                        <option key={cls._id} value={cls._id}>{cls.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Salle</label>
                    <select
                      name="room"
                      value={formData.room}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Sans salle</option>
                      {rooms.map(room => {
                        const isOccupied = occupiedRooms.includes(room._id);
                        return (
                          <option
                            key={room._id}
                            value={room._id}
                            className={isOccupied ? 'text-red-600' : 'text-green-600'}
                          >
                            {isOccupied ? '🔴' : '✅'} {room.name} ({room.capacity} places) {isOccupied ? '- OCCUPÉE' : '- LIBRE'}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="LECTURE">Cours</option>
                      <option value="TUTORIAL">TD</option>
                      <option value="PRACTICAL">TP</option>
                      <option value="INTEGRATED">Cours Intégré (CI)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Jour</label>
                    <select
                      name="dayOfWeek"
                      value={formData.dayOfWeek}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      {days.map((day, index) => (
                        <option key={index} value={index + 1}>{day}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Horaire</label>
                    <select
                      name="timeSlot"
                      value={formData.timeSlot}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      {timeSlots.map((slot, index) => (
                        <option key={index} value={index + 1}>{slot}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Semestre</label>
                    <select
                      name="semester"
                      value={formData.semester}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      {[1, 2].map(sem => (
                        <option key={sem} value={sem}>Semestre {sem}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Groupe {formData.type === 'PRACTICAL' ? '(requis pour TP)' : '(optionnel)'}
                  </label>
                  <input
                    type="text"
                    name="group"
                    value={formData.group}
                    onChange={handleChange}
                    placeholder="Ex: Groupe 1"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required={formData.type === 'PRACTICAL'}
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500"
                  >
                    {editingSession?._id ? 'Modifier' : 'Ajouter'}
                  </button>
                  {editingSession?._id && (
                    <button
                      type="button"
                      onClick={() => confirmDelete(editingSession._id)}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500"
                    >
                      Supprimer
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:ring-2 focus:ring-gray-500"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {tooltip.show && (
          <div
            className="fixed z-50 pointer-events-none"
            style={{
              left: tooltip.x,
              top: tooltip.y,
              transform: 'translateX(-50%) translateY(-100%)'
            }}
          >
            {tooltip.content}
          </div>
        )}

        {conflictAlert.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-medium text-red-600">⚠️ Conflit détecté</h3>
                <button onClick={closeConflictAlert} className="text-gray-400 hover:text-gray-600">×</button>
              </div>
              <div className="p-4">
                <div className="text-sm text-gray-700">
                  {conflictAlert.message.split('\n').map((line, index) => (
                    <div key={index} className={line.startsWith('•') ? 'ml-4 text-red-600' : 'font-medium'}>
                      {line}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {deleteConfirm.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-medium text-red-600">🗑️ Confirmer la suppression</h3>
                <button onClick={cancelDelete} className="text-gray-400 hover:text-gray-600">×</button>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-700 mb-4">Êtes-vous sûr de vouloir supprimer cette séance ? Cette action est irréversible.</p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={cancelDelete}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showValidation && validationResults && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className={`text-lg font-medium ${
                  validationResults.isCompliant ? 'text-green-600' : 'text-red-600'
                }`}>
                  {validationResults.isCompliant ? '✅ Emploi du temps conforme' : '⚠️ Non-conformités détectées'}
                </h3>
                <button onClick={() => setShowValidation(false)} className="text-gray-400 hover:text-gray-600">×</button>
              </div>
              <div className="p-4">
                {validationResults.isCompliant ? (
                  <div className="text-center py-8">
                    <div className="text-6xl mb-4">🎉</div>
                    <p className="text-lg text-green-600 font-medium">L'emploi du temps respecte toutes les contraintes !</p>
                  </div>
                ) : (
                  <div>
                    <div className="mb-4 p-3 bg-red-50 rounded-lg">
                      <p className="text-sm text-red-800">
                        <strong>{validationResults.totalViolations}</strong> violation(s) détectée(s)
                      </p>
                    </div>
                    <div className="space-y-3">
                      {validationResults.violations.map((violation, index) => (
                        <div key={index} className="p-3 border border-red-200 rounded-lg bg-red-50">
                          <div className="font-medium text-red-800">{violation.course}</div>
                          <div className="text-sm text-red-600">{violation.message}</div>
                          <div className="text-xs text-red-500 mt-1">
                            Type: {violation.type.replace('_', ' ')}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex justify-end mt-4">
                  <button
                    onClick={() => setShowValidation(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Timetables;